import type { ComponentType, PropsWithChildren, ReactElement, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import * as RadixSelect from '@radix-ui/react-select';

import { ArrowDown } from '../../icons/mono/ArrowDown';
import { Overlay } from '../Overlays';

export type SelectOptionProps = RadixSelect.SelectItemProps & {
  Icon?: ComponentType<{ className?: string; value: string }> | ReactElement;
};

export const SelectOption = forwardRef(
  ({ children, Icon, className, ...props }: SelectOptionProps, ref: Ref<HTMLDivElement>) => {
    return (
      <RadixSelect.Item
        className={classNames(
          'msq-select-list-item data-[disabled]:msq-select-list-item-disabled data-[state=checked]:msq-select-list-item-active',
          className,
        )}
        {...props}
        ref={ref}
      >
        {Icon &&
          (typeof Icon === 'function' ? (
            <Icon value={props.value} className="size-5 mr-2" />
          ) : (
            Icon
          ))}
        <span className="truncate">
          <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
        </span>
      </RadixSelect.Item>
    );
  },
);

type PortalMode = 'none' | 'default' | 'overlay';

export interface SelectProps
  extends Pick<RadixSelect.SelectProps, 'name' | 'value' | 'onValueChange' | 'disabled'> {
  placeholder?: ReactNode;
  children?: ReactNode;
  className?: string;
  ValueIcon?: ComponentType<{ className?: string; value?: string }>;
  popperWidth?: 'auto' | 'full';
  portalMode?: PortalMode;
}

const SelectContentPortal = ({
  mode: portalMode,
  children,
}: PropsWithChildren<{ mode: PortalMode }>) => {
  switch (portalMode) {
    case 'none':
      return children;

    case 'default':
      return <RadixSelect.Portal>{children}</RadixSelect.Portal>;

    case 'overlay':
      return <Overlay>{children}</Overlay>;
  }
};

export const Select = ({
  className,
  children,
  value,
  name,
  disabled,
  ValueIcon,
  onValueChange,
  popperWidth = 'full',
  placeholder = 'Select',
  portalMode = 'default',
}: SelectProps) => {
  return (
    <RadixSelect.Root disabled={disabled} name={name} onValueChange={onValueChange} value={value}>
      <RadixSelect.Trigger
        className={classNames('msq-input pl-2 flex items-center group', className)}
      >
        {ValueIcon && (
          <span className="inline-flex items-center justify-center flex-shrink-0 size-6">
            <ValueIcon value={value} className="size-4" />
          </span>
        )}
        <span className="truncate pl-1">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon className="text-base-text-tertiary group-disabled:text-base-text-quaternary ml-auto group-data-[state=open]:rotate-180 transition-transform flex items-center justify-center size-6">
          <ArrowDown className="size-[0.875rem]" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <SelectContentPortal mode={portalMode}>
        <RadixSelect.Content
          sideOffset={4}
          position="popper"
          className={classNames(
            // NOTE: close animation is not supported https://github.com/radix-ui/primitives/issues/1893
            // data-[state=closed]:animate-fade-and-slide-out-to-top
            'z-[100] data-[side=bottom]:animate-fade-and-slide-in-from-top',
            'data-[side=top]:animate-fade-and-slide-in-from-bottom',
            'msq-select-list max-h-[300px] overflow-auto max-w-[var(--radix-select-trigger-width)]',
            {
              'w-[var(--radix-select-trigger-width)]': popperWidth === 'full',
            },
          )}
        >
          <RadixSelect.Viewport className="msq-select-list-content">
            {children}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </SelectContentPortal>
    </RadixSelect.Root>
  );
};
